import {
  collection,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";
import { db, functions } from "../../firebase";

type StudentRow = {
  id: string;
  studentKey: string;
  firstName: string;
  lastName: string;
  grade: string;
  familyId?: string;
  isActive: boolean;
};

type ParentRow = {
  id: string;
  parentKey: string;
  firstName: string;
  lastName: string;
  email?: string;
  parentEmail?: string;
  familyId?: string;
  isActive: boolean;
};

type FamilyRow = {
  id: string;
  familyName?: string;
};

type LinkRow = {
  id: string;
  studentKey?: string;
  parentKey?: string;
  parentEmail?: string | null;
  parentUid?: string | null;
  relationship?: string;
  isActive?: boolean;
};

function fullName(r: { firstName?: string; lastName?: string }) {
  return `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim();
}

export function RosterLinksPage() {
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [parents, setParents] = useState<ParentRow[]>([]);
  const [families, setFamilies] = useState<FamilyRow[]>([]);
  const [links, setLinks] = useState<LinkRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const [studentId, setStudentId] = useState("");
  const [parentId, setParentId] = useState("");
  const [relationship, setRelationship] = useState("Parent");
  const [filter, setFilter] = useState("");
  const [savingParentLink, setSavingParentLink] = useState(false);
  const [deletingLinkId, setDeletingLinkId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [sSnap, pSnap, fSnap, lSnap] = await Promise.all([
        getDocs(query(collection(db, "students"), orderBy("lastName", "asc"))),
        getDocs(query(collection(db, "parents"), orderBy("lastName", "asc"))),
        getDocs(query(collection(db, "families"), orderBy("familyName", "asc"))),
        getDocs(collection(db, "studentParentLinks")),
      ]);

      setStudents(sSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
      setParents(pSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
      setFamilies(fSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
      setLinks(lSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
    } catch (e: any) {
      setErr(e?.message || "Failed to load roster docs.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const familyLabelById = useMemo(() => {
    const out: Record<string, string> = {};
    families.forEach((f) => {
      out[f.id] = f.familyName || f.id;
    });
    return out;
  }, [families]);

  const studentLabelByKey = useMemo(() => {
    const out: Record<string, string> = {};
    students.forEach((s) => {
      const key = s.studentKey || s.id;
      const family = s.familyId ? familyLabelById[s.familyId] || s.familyId : "";
      out[key] = `${fullName(s) || key}${s.grade ? ` (G${s.grade})` : ""}${family ? ` - ${family}` : ""}`;
    });
    return out;
  }, [familyLabelById, students]);

  const parentLabelByKey = useMemo(() => {
    const out: Record<string, string> = {};
    parents.forEach((p) => {
      const key = p.parentKey || p.id;
      const emailValue = p.email ?? p.parentEmail ?? "";
      const family = p.familyId ? familyLabelById[p.familyId] || p.familyId : "No family";
      out[key] = `${fullName(p) || key}${emailValue ? ` - ${emailValue}` : ""} - ${family}`;
    });
    return out;
  }, [familyLabelById, parents]);

  const sortedLinks = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return [...links].filter((link) => {
      if (!f) return true;
      const student = students.find((s) => (s.studentKey || s.id) === link.studentKey);
      const parent = parents.find((p) => (p.parentKey || p.id) === link.parentKey);
      const hay = [
        link.studentKey,
        link.parentKey,
        link.parentEmail,
        link.relationship,
        student ? fullName(student) : "",
        student?.familyId,
        student?.familyId ? familyLabelById[student.familyId] : "",
        parent ? fullName(parent) : "",
        parent?.email,
        parent?.parentEmail,
        parent?.familyId,
        parent?.familyId ? familyLabelById[parent.familyId] : "",
      ].join(" ").toLowerCase();
      return hay.includes(f);
    }).sort((a, b) => {
      const aLabel = `${studentLabelByKey[a.studentKey || ""] || a.studentKey || ""} ${parentLabelByKey[a.parentKey || ""] || a.parentKey || ""}`;
      const bLabel = `${studentLabelByKey[b.studentKey || ""] || b.studentKey || ""} ${parentLabelByKey[b.parentKey || ""] || b.parentKey || ""}`;
      return aLabel.localeCompare(bLabel);
    });
  }, [familyLabelById, filter, links, parentLabelByKey, parents, studentLabelByKey, students]);

  async function createStudentParentLink() {
    setErr(null);
    setMsg(null);
    setSavingParentLink(true);
    try {
      if (!studentId || !parentId) throw new Error("Pick a student and a parent.");

      const fn = httpsCallable(functions, "upsertStudentParentLinkAdmin");
      const res: any = await fn({
        studentDocId: studentId,
        parentDocId: parentId,
        relationship,
      });

      setMsg(res?.data?.created ? "Created Parent-Student link." : "Updated existing Parent-Student link.");
      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed creating parent link.");
    } finally {
      setSavingParentLink(false);
    }
  }

  async function removeStudentParentLink(link: LinkRow) {
    const studentLabel = studentLabelByKey[link.studentKey || ""] || link.studentKey || "student";
    const parentLabel = parentLabelByKey[link.parentKey || ""] || link.parentEmail || link.parentKey || "parent";
    if (!confirm(`Remove Parent-Student link?\n\nStudent: ${studentLabel}\nParent: ${parentLabel}\n\nThis removes explicit guardian messaging/access for this pairing.`)) return;

    setErr(null);
    setMsg(null);
    setDeletingLinkId(link.id);
    try {
      const fn = httpsCallable(functions, "deleteStudentParentLinkAdmin");
      await fn({ linkDocId: link.id, studentKey: link.studentKey, parentKey: link.parentKey });
      setMsg("Removed Parent-Student link.");
      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed removing parent link.");
    } finally {
      setDeletingLinkId(null);
    }
  }

  return (
    <div className="card">
      <h2>Roster - Links</h2>
      <p className="muted">
        Parent-Student links grant student-specific messaging and roster access without changing the student's primary
        family. Use this for split households, outside guardians, and adults who should see one student but not the
        whole household.
      </p>
      <p className="muted">
        Teacher and staff access is managed manually on the <b>Teachers / Staff</b> page.
      </p>

      {err ? <div className="error">{err}</div> : null}
      {msg ? <div className="success">{msg}</div> : null}

      {loading ? (
        <div className="muted">Loading...</div>
      ) : (
        <>
          <hr className="hr" />

          <h3>Additional Guardian Access</h3>
          <div className="success">
            Split-family workflow: create the parent with no family on Roster - Parents, then link that parent here to
            each student they should access.
          </div>
          <div className="grid2">
            <div>
              <label className="label">Student</label>
              <select className="input" value={studentId} onChange={(e) => setStudentId(e.target.value)}>
                <option value="">Select...</option>
                {students
                  .filter((s) => s.isActive !== false)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {fullName(s)} (G{s.grade}) - {s.studentKey}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="label">Parent</label>
              <select className="input" value={parentId} onChange={(e) => setParentId(e.target.value)}>
                <option value="">Select...</option>
                {parents
                  .filter((p) => p.isActive !== false)
                  .map((p) => {
                    const emailValue = p.email ?? p.parentEmail ?? "";
                    return (
                      <option key={p.id} value={p.id}>
                        {fullName(p)} - {emailValue} ({p.parentKey})
                      </option>
                    );
                  })}
              </select>
            </div>

            <div>
              <label className="label">Relationship</label>
              <select className="input" value={relationship} onChange={(e) => setRelationship(e.target.value)}>
                <option value="Parent">Parent</option>
                <option value="Guardian">Guardian</option>
                <option value="Mother">Mother</option>
                <option value="Father">Father</option>
                <option value="Step-parent">Step-parent</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <button
            className="btn"
            disabled={savingParentLink}
            onClick={createStudentParentLink}
            style={{ marginTop: 10 }}
          >
            {savingParentLink ? "Saving..." : "Save Parent-Student Link"}
          </button>

          <hr className="hr" />

          <h3>Existing Parent-Student Links</h3>
          <div style={{ marginBottom: 10 }}>
            <label className="label">Search links</label>
            <input
              className="input"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Student, parent, email, relationship, or family..."
            />
          </div>
          {sortedLinks.length === 0 ? (
            <div className="muted">No parent-student links found.</div>
          ) : (
            <div className="table">
              <div className="thead" style={{ gridTemplateColumns: "1fr 1fr 140px 100px" }}>
                <div>Student</div>
                <div>Parent</div>
                <div>Relationship</div>
                <div></div>
              </div>
              {sortedLinks.map((link) => (
                <div className="trow" key={link.id} style={{ gridTemplateColumns: "1fr 1fr 140px 100px" }}>
                  <div>{studentLabelByKey[link.studentKey || ""] || link.studentKey || "-"}</div>
                  <div>{parentLabelByKey[link.parentKey || ""] || link.parentEmail || link.parentKey || "-"}</div>
                  <div>{link.relationship || "Parent"}</div>
                  <div style={{ textAlign: "right" }}>
                    <button
                      className="btn small secondary"
                      disabled={deletingLinkId === link.id}
                      onClick={() => removeStudentParentLink(link)}
                    >
                      {deletingLinkId === link.id ? "Working..." : "Remove"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <hr className="hr" />

          <h3>Bulk Import Option</h3>
          <div className="muted">
            For larger updates, use <b>Roster - Roster Import (CSV)</b> and choose the{" "}
            <b>Parent-Student Links</b> template.
          </div>
        </>
      )}
    </div>
  );
}
